import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { tenantsAPI, customersAPI, quotesAPI, contractsAPI, adminAPI } from '../services/api';
import { useAuth } from '../auth/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { CountrySelect } from '../components/CountrySelect';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { ArrowLeft, Save, PowerOff, Power, Trash2, Loader2, AlertCircle, CheckCircle2, FileText, Plus, Phone, Smartphone, Mail, XCircle, Star, FileSignature, Clock, AlertTriangle, Shield, Upload, Download, FileDown, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { ContactsTab } from '../components/ContactsTab';
import { AddressFields } from '../components/AddressFields';

function TenantContactsTab({ tenant, onReload }) {
  return (
    <ContactsTab
      contacts={tenant.contacts || []}
      onAdd={data => tenantsAPI.addContact(tenant.id, data).then(r => r.data)}
      onUpdate={(cid, data) => tenantsAPI.updateContact(tenant.id, cid, data).then(r => r.data)}
      onDelete={cid => tenantsAPI.deleteContact(tenant.id, cid)}
      noun="tenant"
    />
  );
}

const NAVY   = 'hsl(var(--c4p-navy-900))';
const ORANGE = 'hsl(var(--c4p-orange-600))';
const CURRENCIES = ['GBP', 'EUR', 'USD', 'CAD', 'AUD'];
const COUNTRIES  = ['United Kingdom', 'Germany', 'France', 'Netherlands', 'Spain', 'Italy', 'United States', 'Canada', 'Australia', 'UAE', 'South Africa', 'Other'];
const CURRENCY_SYMBOLS = { GBP: '\u00a3', EUR: '\u20ac', USD: '$', CAD: 'CA$', AUD: 'A$' };
const STATUS_STYLES = { DRAFT: { bg: '#f8fafc', color: '#475569', border: '#e2e8f0' }, SENT: { bg: '#f0f9ff', color: '#0c4a6e', border: '#bae6fd' }, ACCEPTED: { bg: '#f0fdf4', color: '#14532d', border: '#bbf7d0' }, EXPIRED: { bg: '#fffbeb', color: '#78350f', border: '#fde68a' } };

const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '\u2014';
const fmtMoney = (n, code) => `${CURRENCY_SYMBOLS[code] || ''}${Number(n || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const StatusBadge = ({ status }) => { const s = STATUS_STYLES[status] || STATUS_STYLES.DRAFT; return <span className="text-xs font-medium px-2 py-0.5 rounded-full" style={{ background: s.bg, color: s.color, border: `1px solid ${s.border}` }}>{status}</span>; };

const PRODUCT_DISPLAY_T = { STANDARD: 'Standard', ADVANCED: 'Advanced', MTPRO: 'MTPro' };
const ProductBadgeT = ({ code }) => (
  <span className="text-xs font-medium px-2 py-0.5 rounded-full whitespace-nowrap"
    style={code === 'STANDARD'
      ? { background: 'hsl(var(--muted))', color: 'hsl(var(--fg))' }
      : { background: NAVY, color: 'white' }}>
    {PRODUCT_DISPLAY_T[code] || code}
  </span>
);

const CategoryBadgeT = ({ category }) => {
  if (!category || category === 'PRODUCTION') return null;
  const styles = {
    TEST_SYSTEM: { background: '#fff7ed', color: '#ea580c', border: '1px solid #fdba74' },
    BESPOKE:     { background: '#f5f3ff', color: '#7c3aed', border: '1px solid #c4b5fd' },
  };
  const labels = { TEST_SYSTEM: 'Test System', BESPOKE: 'Custom' };
  return <span className="text-xs font-medium px-2 py-0.5 rounded-full whitespace-nowrap" style={styles[category] || {}}>{labels[category] || category}</span>;
};

const ExpiryCellT = ({ expiryDate }) => {
  if (!expiryDate) return <span style={{ color: 'hsl(var(--muted-fg))' }}>-</span>;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const exp = new Date(expiryDate); exp.setHours(0, 0, 0, 0);
  const diffDays = Math.round((exp - today) / 86400000);
  const fmtd = exp.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  if (diffDays < 0) return <span className="flex items-center gap-1 text-xs font-medium" style={{ color: '#b91c1c' }}><AlertTriangle size={11} />{fmtd}</span>;
  if (diffDays <= 7) return <span className="flex items-center gap-1 text-xs font-medium" style={{ color: '#d97706' }}><Clock size={11} />{fmtd}</span>;
  return <span className="text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>{fmtd}</span>;
};

const billingLabelT = (model) => model === 'TENANT_DIRECT' ? 'Tenant Direct' : model === 'DIRECT' ? 'Direct' : 'Partner';

function TenantGdprExportButton({ tenantId, tenantName }) {
  const [exporting, setExporting] = useState(false);
  const handleExport = async () => {
    setExporting(true);
    try {
      const { data } = await adminAPI.exportTenantData(tenantId);
      const blob = new Blob([data], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `gdpr_export_${(tenantName || 'tenant').replace(/\s+/g, '_')}_${new Date().toISOString().slice(0,10)}.json`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('GDPR data export downloaded');
    } catch { toast.error('Failed to generate GDPR export'); }
    finally { setExporting(false); }
  };
  return (
    <Button variant="outline" size="sm" onClick={handleExport} disabled={exporting}
      title="Download all personal data held for this tenant (GDPR SAR)"
      data-testid="tenant-gdpr-export-btn"
      style={{ borderColor: '#d1d5db', color: '#64748b' }}>
      {exporting ? <Loader2 size={12} className="animate-spin mr-1" /> : <ShieldCheck size={12} className="mr-1" />}
      GDPR Export
    </Button>
  );
}

function ProfileTab({ tenant, partners, onSaved }) {
  const [form, setForm] = useState({ ...tenant });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved]   = useState(false);
  const [err, setErr]       = useState(null);
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'SUPER_ADMIN';
  const [showMsaForm, setShowMsaForm] = useState(false);
  const [msaVersion, setMsaVersion]   = useState('');
  const [msaDate, setMsaDate]         = useState('');
  const [savingMsa, setSavingMsa]     = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [downloadingDoc, setDownloadingDoc] = useState(false);
  const [savingBillingType, setSavingBillingType] = useState(false);
  useEffect(() => { setForm({ ...tenant }); }, [tenant]);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true); setErr(null); setSaved(false);
    try {
      const partner = partners.find(p => p.id === form.partner_id);
      await tenantsAPI.update(tenant.id, { ...form, partner_name: partner?.company_name || form.partner_name || null, partner_id: form.partner_id || null });
      toast.success('Tenant saved'); setSaved(true); setTimeout(() => setSaved(false), 3000); onSaved();
    } catch (ex) { setErr(ex.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const handleMsaSave = async (e) => {
    e.preventDefault();
    setSavingMsa(true);
    try {
      await tenantsAPI.updateMsa(tenant.id, {
        msa_version: msaVersion.trim() || null,
        msa_signed_date: msaDate || null,
      });
      toast.success('MSA recorded as signed');
      setShowMsaForm(false);
      onSaved();
    } catch (ex) {
      toast.error(ex.response?.data?.detail || 'Failed to record MSA');
    } finally { setSavingMsa(false); }
  };

  const handleMsaDocUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingDoc(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await tenantsAPI.uploadMsaDoc(tenant.id, fd);
      toast.success('MSA document uploaded successfully');
      onSaved();
    } catch (ex) {
      toast.error(ex.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadingDoc(false);
      e.target.value = '';
    }
  };

  const handleMsaDocDownload = async () => {
    setDownloadingDoc(true);
    try {
      const { data, headers } = await tenantsAPI.getMsaDoc(tenant.id);
      const contentType = headers['content-type'] || 'application/octet-stream';
      const blob = new Blob([data], { type: contentType });
      const url = URL.createObjectURL(blob);
      if (contentType === 'application/pdf') {
        window.open(url, '_blank');
        setTimeout(() => URL.revokeObjectURL(url), 10000);
      } else {
        const a = document.createElement('a');
        a.href = url;
        a.download = tenant.msa_document?.filename || 'msa_document';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
    } catch (ex) {
      toast.error('Failed to download MSA document');
    } finally { setDownloadingDoc(false); }
  };

  const handleBillingTypeChange = async (newValue) => {
    setSavingBillingType(true);
    try {
      await tenantsAPI.setBillingViaPartner(tenant.id, newValue);
      toast.success(newValue ? 'Set to Partner Billing — no tenant MSA required' : 'Set to Direct Billing — tenant MSA now required');
      onSaved();
    } catch (ex) {
      toast.error(ex.response?.data?.detail || 'Failed to update billing type');
    } finally { setSavingBillingType(false); }
  };

  return (
    <div className="space-y-6 pt-2">
      {err && <Alert variant="destructive"><AlertCircle className="h-4 w-4" /><AlertDescription>{err}</AlertDescription></Alert>}

      {/* ── Billing Type (MSA Requirement) — Super Admin only ── */}
      <div className="rounded-xl border p-4 space-y-3" data-testid="tenant-billing-type-section"
        style={{ borderColor: '#e2e8f0', background: '#f8fafc' }}>
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-2.5 min-w-0">
            <ShieldCheck size={15} style={{ color: NAVY, marginTop: 2, flexShrink: 0 }} />
            <div>
              <p className="text-sm font-semibold" style={{ color: NAVY }}>Billing Arrangement</p>
              <p className="text-xs mt-0.5" style={{ color: 'hsl(var(--muted-fg))' }}>
                Controls whether this tenant requires its own MSA with C4P for signing.
                {!isSuperAdmin && ' Contact a Super Admin to change this.'}
              </p>
            </div>
          </div>
          {/* Status pill — always visible */}
          <span className="text-xs font-medium px-2.5 py-1 rounded-full flex-shrink-0 flex items-center gap-1"
            style={tenant.billing_via_partner !== false
              ? { background: '#eff6ff', color: '#1e40af', border: '1px solid #bfdbfe' }
              : { background: '#fff7ed', color: '#9a3412', border: '1px solid #fdba74' }}
            data-testid="tenant-billing-type-badge">
            {tenant.billing_via_partner !== false ? 'Partner Billing' : 'Direct Billing (MSA required)'}
          </span>
        </div>

        <div className="grid grid-cols-2 gap-2">
          {/* Partner Billing option */}
          <button
            type="button"
            onClick={() => isSuperAdmin && tenant.billing_via_partner !== true && handleBillingTypeChange(true)}
            disabled={savingBillingType || !isSuperAdmin}
            data-testid="billing-via-partner-btn"
            className="rounded-lg p-3 text-left transition-all"
            style={{
              border: `2px solid ${tenant.billing_via_partner !== false ? '#1e40af' : '#e2e8f0'}`,
              background: tenant.billing_via_partner !== false ? '#eff6ff' : 'white',
              opacity: savingBillingType ? 0.6 : 1,
              cursor: isSuperAdmin ? 'pointer' : 'default',
            }}>
            <div className="flex items-center gap-2 mb-1">
              <div className="w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0"
                style={{ borderColor: tenant.billing_via_partner !== false ? '#1e40af' : '#94a3b8' }}>
                {tenant.billing_via_partner !== false && (
                  <div className="w-2 h-2 rounded-full" style={{ background: '#1e40af' }} />
                )}
              </div>
              <p className="text-xs font-semibold" style={{ color: NAVY }}>Partner Billing</p>
            </div>
            <p className="text-xs" style={{ color: 'hsl(var(--muted-fg))', paddingLeft: '24px' }}>
              Tenant is billed through Partner. Partner MSA with C4P covers this tenant. No separate tenant MSA needed.
            </p>
          </button>

          {/* Direct Billing option */}
          <button
            type="button"
            onClick={() => isSuperAdmin && tenant.billing_via_partner !== false && handleBillingTypeChange(false)}
            disabled={savingBillingType || !isSuperAdmin}
            data-testid="billing-direct-btn"
            className="rounded-lg p-3 text-left transition-all"
            style={{
              border: `2px solid ${tenant.billing_via_partner === false ? '#ea580c' : '#e2e8f0'}`,
              background: tenant.billing_via_partner === false ? '#fff7ed' : 'white',
              opacity: savingBillingType ? 0.6 : 1,
              cursor: isSuperAdmin ? 'pointer' : 'default',
            }}>
            <div className="flex items-center gap-2 mb-1">
              <div className="w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0"
                style={{ borderColor: tenant.billing_via_partner === false ? '#ea580c' : '#94a3b8' }}>
                {tenant.billing_via_partner === false && (
                  <div className="w-2 h-2 rounded-full" style={{ background: '#ea580c' }} />
                )}
              </div>
              <p className="text-xs font-semibold" style={{ color: NAVY }}>Direct Billing</p>
            </div>
            <p className="text-xs" style={{ color: 'hsl(var(--muted-fg))', paddingLeft: '24px' }}>
              C4P invoices this tenant directly. Tenant must have its own signed MSA regardless of billing model on the quote.
            </p>
          </button>
        </div>
        {savingBillingType && <p className="text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>Saving…</p>}
      </div>

      {/* ── MSA Status Section ── */}
      <div className="rounded-xl border p-4 space-y-3" data-testid="tenant-msa-section"
        style={{ background: tenant.msa_signed ? '#f0fdf4' : '#fef2f2', borderColor: tenant.msa_signed ? '#bbf7d0' : '#fecaca' }}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Shield size={14} style={{ color: tenant.msa_signed ? '#16a34a' : '#dc2626' }} />
            <span className="text-sm font-semibold" style={{ color: tenant.msa_signed ? '#14532d' : '#991b1b' }}>
              Master Service Agreement
            </span>
          </div>
          {tenant.msa_signed ? (
            <span className="text-xs font-medium px-2 py-0.5 rounded-full"
              style={{ background: '#dcfce7', color: '#14532d', border: '1px solid #bbf7d0' }}
              data-testid="tenant-msa-signed-badge">
              Signed
            </span>
          ) : (
            <span className="text-xs font-medium px-2 py-0.5 rounded-full"
              style={{ background: '#fee2e2', color: '#991b1b', border: '1px solid #fecaca' }}
              data-testid="tenant-msa-unsigned-badge">
              Not Signed
            </span>
          )}
        </div>
        {tenant.msa_signed && (
          <div className="text-xs space-y-0.5" style={{ color: '#166534' }}>
            {tenant.msa_version && <div>Version: <span className="font-medium">{tenant.msa_version}</span></div>}
            {tenant.msa_signed_date && <div>Date: <span className="font-medium">{tenant.msa_signed_date}</span></div>}
            {tenant.msa_signed_by_name && <div>Recorded by: <span className="font-medium">{tenant.msa_signed_by_name}</span></div>}
          </div>
        )}
        {!tenant.msa_signed && (
          <p className="text-xs" style={{ color: '#991b1b' }}>
            {tenant.billing_via_partner === false
              ? 'This tenant is configured for Direct Billing — an MSA must be recorded as signed before any quote can be sent for signing.'
              : 'Required when this tenant has Tenant Direct billing quotes. A Super Admin must record the MSA as signed.'}
          </p>
        )}

        {/* ── MSA Document (upload / download) ── */}
        <div className="pt-1">
          {tenant.msa_document ? (
            <div className="flex items-center gap-2 flex-wrap">
              <div className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg flex-1 min-w-0"
                style={{ background: 'white', border: '1px solid #d1fae5', color: '#065f46' }}>
                <FileDown size={12} className="flex-shrink-0" />
                <span className="truncate font-medium">{tenant.msa_document.filename}</span>
                <span className="flex-shrink-0" style={{ color: '#6ee7b7' }}>
                  &middot; {tenant.msa_document.uploaded_at ? new Date(tenant.msa_document.uploaded_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : ''}
                </span>
              </div>
              <Button size="sm" type="button" variant="outline" onClick={handleMsaDocDownload}
                disabled={downloadingDoc}
                style={{ borderColor: '#6ee7b7', color: '#065f46', flexShrink: 0 }}
                data-testid="tenant-msa-doc-download-btn">
                {downloadingDoc ? <Loader2 size={12} className="animate-spin mr-1" /> : <Download size={12} className="mr-1" />}
                View / Download
              </Button>
              {isSuperAdmin && (
                <label className="cursor-pointer flex-shrink-0">
                  <input type="file" accept=".pdf,.doc,.docx" className="hidden"
                    onChange={handleMsaDocUpload} disabled={uploadingDoc}
                    data-testid="tenant-msa-doc-replace-input" />
                  <Button size="sm" type="button" variant="outline" asChild disabled={uploadingDoc}
                    style={{ borderColor: '#d1d5db', color: '#6b7280', pointerEvents: uploadingDoc ? 'none' : 'auto' }}>
                    <span>
                      {uploadingDoc ? <Loader2 size={12} className="animate-spin mr-1" /> : <Upload size={12} className="mr-1" />}
                      Replace
                    </span>
                  </Button>
                </label>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <p className="text-xs flex-1" style={{ color: tenant.msa_signed ? '#166534' : '#991b1b', opacity: 0.8 }}>
                No document uploaded yet.
              </p>
              {isSuperAdmin && (
                <label className="cursor-pointer flex-shrink-0">
                  <input type="file" accept=".pdf,.doc,.docx" className="hidden"
                    onChange={handleMsaDocUpload} disabled={uploadingDoc}
                    data-testid="tenant-msa-doc-upload-input" />
                  <Button size="sm" type="button" variant="outline" asChild disabled={uploadingDoc}
                    style={{ borderColor: tenant.msa_signed ? '#86efac' : '#fca5a5', color: tenant.msa_signed ? '#15803d' : '#b91c1c', pointerEvents: uploadingDoc ? 'none' : 'auto' }}>
                    <span>
                      {uploadingDoc ? <Loader2 size={12} className="animate-spin mr-1" /> : <Upload size={12} className="mr-1" />}
                      Upload Signed MSA
                    </span>
                  </Button>
                </label>
              )}
            </div>
          )}
        </div>
        {isSuperAdmin && !showMsaForm && (
          <Button size="sm" type="button" variant="outline" onClick={() => setShowMsaForm(true)}
            style={{ borderColor: tenant.msa_signed ? '#86efac' : '#fca5a5', color: tenant.msa_signed ? '#15803d' : '#b91c1c' }}
            data-testid="tenant-msa-record-btn">
            <Shield size={12} className="mr-1.5" />
            {tenant.msa_signed ? 'Update MSA Record' : 'Record MSA as Signed'}
          </Button>
        )}
        {isSuperAdmin && showMsaForm && (
          <form onSubmit={handleMsaSave} className="space-y-3 pt-2 border-t"
            style={{ borderColor: tenant.msa_signed ? '#bbf7d0' : '#fecaca' }}>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <label className="text-xs font-medium" style={{ color: '#475569' }}>Version / Reference</label>
                <Input value={msaVersion} onChange={e => setMsaVersion(e.target.value)}
                  placeholder="e.g. Tenant Agreement v1.2" className="h-8 text-xs"
                  data-testid="tenant-msa-version-input" />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium" style={{ color: '#475569' }}>Date Signed</label>
                <Input type="date" value={msaDate} onChange={e => setMsaDate(e.target.value)}
                  className="h-8 text-xs" data-testid="tenant-msa-date-input" />
              </div>
            </div>
            <div className="flex gap-2">
              <Button type="submit" size="sm" disabled={savingMsa}
                style={{ background: '#16a34a', color: 'white' }}
                data-testid="tenant-msa-confirm-btn">
                {savingMsa ? <Loader2 size={11} className="animate-spin mr-1" /> : null}
                Confirm
              </Button>
              <Button type="button" size="sm" variant="outline"
                onClick={() => { setShowMsaForm(false); setMsaVersion(''); setMsaDate(''); }}>
                Cancel
              </Button>
            </div>
          </form>
        )}
      </div>

      <form id="tenant-profile-form" onSubmit={handleSave} className="space-y-6">

        {/* ── Top Save bar ── */}
        <div className="flex items-center gap-3">
          <Button type="submit" disabled={saving} style={{ background: NAVY, color: 'white' }} className="gap-2">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save size={14} />} Save Changes
          </Button>
          {saved && <span className="flex items-center gap-1 text-sm" style={{ color: '#15803d' }}><CheckCircle2 size={14} /> Saved</span>}
        </div>

        <div className="rounded-xl border bg-white p-5 space-y-4">
          <h3 className="text-sm font-bold" style={{ color: NAVY }}>Tenant Details</h3>
          <div className="space-y-1.5"><Label>Company Name *</Label><Input value={form.company_name || ''} onChange={e => setForm(f => ({ ...f, company_name: e.target.value }))} required /></div>
          {/* Tenant type + Website */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Tenant Type</Label>
              <Select value={form.tenant_type || 'standard'} onValueChange={v => setForm(f => ({ ...f, tenant_type: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="standard">Standard</SelectItem>
                  <SelectItem value="mtpro">MTPro (Multi-Tenant)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Website</Label>
              <Input value={form.website || ''} onChange={e => setForm(f => ({ ...f, website: e.target.value }))} placeholder="https://" />
            </div>
          </div>
          {(form.tenant_type === 'mtpro') && (
            <div className="space-y-1.5">
              <Label>User Count <span className="text-xs font-normal" style={{ color: 'hsl(var(--muted-fg))' }}>- users allocated on MTPro system</span></Label>
              <Input type="number" min="0" placeholder="0" value={form.num_users || ''}
                onChange={e => setForm(f => ({ ...f, num_users: parseInt(e.target.value) || 0 }))} />
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label>Contact Name</Label><Input value={form.contact_name || ''} onChange={e => setForm(f => ({ ...f, contact_name: e.target.value }))} /></div>
            <div className="space-y-1.5"><Label className="flex items-center gap-1"><Mail size={12} /> Email</Label><Input type="email" value={form.email || ''} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} /></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label className="flex items-center gap-1"><Smartphone size={12} /> Mobile</Label><Input value={form.phone_mobile || ''} onChange={e => setForm(f => ({ ...f, phone_mobile: e.target.value }))} placeholder="+44 7700..." /></div>
            <div className="space-y-1.5"><Label className="flex items-center gap-1"><Phone size={12} /> Landline</Label><Input value={form.phone_landline || ''} onChange={e => setForm(f => ({ ...f, phone_landline: e.target.value }))} placeholder="+44 20..." /></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label>Country</Label>
              <CountrySelect value={form.country || ''} onChange={v => setForm(f => ({ ...f, country: v }))} testId="edit-tenant-country" />
            </div>
            <div className="space-y-1.5"><Label>Default Currency</Label>
              <Select value={form.default_currency || 'GBP'} onValueChange={v => setForm(f => ({ ...f, default_currency: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{CURRENCIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          {/* Structured Address */}
          <AddressFields form={form} setForm={setForm} />
          <div className="space-y-1.5"><Label>Notes</Label><Textarea value={form.notes || ''} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} className="h-16 resize-none" /></div>
        </div>
        <div className="rounded-xl border bg-white p-5 space-y-4">
          <h3 className="text-sm font-bold" style={{ color: NAVY }}>Linked Partner</h3>
          <div className="space-y-1.5">
            <Label>Associated SAP Partner</Label>
            <Select value={form.partner_id || 'none'} onValueChange={v => setForm(f => ({ ...f, partner_id: v === 'none' ? null : v }))}>
              <SelectTrigger><SelectValue placeholder="No linked partner" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No linked partner</SelectItem>
                {partners.map(p => <SelectItem key={p.id} value={p.id}>{p.company_name}</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>Links this Tenant to the Partner that manages the commercial relationship.</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button type="submit" disabled={saving} style={{ background: NAVY, color: 'white' }} className="gap-2">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save size={14} />} Save Changes
          </Button>
          {saved && <span className="flex items-center gap-1 text-sm" style={{ color: '#15803d' }}><CheckCircle2 size={14} /> Saved</span>}
        </div>
      </form>
    </div>
  );
}

function QuotesTab({ tenantId, tenantName }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'SUPER_ADMIN';

  const [quotes, setQuotes]             = useState([]);
  const [total, setTotal]               = useState(0);
  const [loading, setLoading]           = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting]         = useState(false);

  const load = useCallback(() => {
    if (!tenantId) return;
    setLoading(true);
    quotesAPI.list({ page_size: 100 }).then(r => {
      const all = r.data.items || [];
      const filtered = all.filter(q => q.tenant_id === tenantId || q.end_customer_name === tenantName);
      setQuotes(filtered); setTotal(filtered.length);
    }).catch(() => setQuotes([])).finally(() => setLoading(false));
  }, [tenantId, tenantName]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await quotesAPI.delete(deleteTarget.id);
      toast.success(`${deleteTarget.quote_number} deleted`);
      setDeleteTarget(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
    finally { setDeleting(false); }
  };

  const canDelete = (q) => q.status !== 'ACCEPTED' || isSuperAdmin;

  if (loading) return <div className="space-y-3 pt-2">{[1,2,3].map(i => <Skeleton key={i} className="h-12 w-full" />)}</div>;

  return (
    <div className="space-y-4 pt-2">
      <p className="text-sm" style={{ color: 'hsl(var(--muted-fg))' }}>
        {total === 0 ? `No quotes for ${tenantName} yet.` : `${total} quote(s) linked to ${tenantName}`}
      </p>
      {quotes.length === 0 ? (
        <div className="rounded-xl border bg-white p-10 text-center">
          <FileText size={28} className="mx-auto mb-3" style={{ color: 'hsl(var(--border))' }} />
          <p className="text-sm font-medium" style={{ color: NAVY }}>No quotes yet</p>
        </div>
      ) : (
        <div className="rounded-xl border bg-white overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow style={{ background: 'hsl(var(--muted))' }}>
                  <TableHead>Quote #</TableHead>
                  <TableHead>Billing</TableHead>
                  <TableHead>Partner</TableHead>
                  <TableHead>Product</TableHead>
                  <TableHead>Users</TableHead>
                  <TableHead>Monthly</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Expiry</TableHead>
                  <TableHead>PSM</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {quotes.map(q => (
                  <TableRow key={q.id} onClick={() => navigate(`/quotes/${q.id}`)}
                    className="data-table-row cursor-pointer transition-colors hover:bg-slate-50">
                    <TableCell>
                      <span className="font-mono text-sm font-semibold" style={{ color: NAVY }}>{q.quote_number}</span>
                      <span className="text-xs ml-1" style={{ color: 'hsl(var(--muted-fg))' }}>v{q.version}</span>
                    </TableCell>
                    <TableCell className="text-xs whitespace-nowrap" style={{ color: 'hsl(var(--muted-fg))' }}>
                      {billingLabelT(q.billing_model)}
                    </TableCell>
                    <TableCell className="text-sm max-w-[130px] truncate">{q.customer_name || '\u2014'}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 flex-wrap">
                        {(!q.quote_category || q.quote_category === 'PRODUCTION') && <ProductBadgeT code={q.product_code} />}
                        <CategoryBadgeT category={q.quote_category} />
                      </div>
                    </TableCell>
                    <TableCell className="text-sm tabular-nums">{q.num_users}</TableCell>
                    <TableCell className="text-sm font-semibold tabular-nums">
                      {q.pricing_snapshot
                        ? fmtMoney(
                            q.billing_model === 'TENANT_DIRECT' && q.pricing_snapshot.tenant_invoice_monthly
                              ? q.pricing_snapshot.tenant_invoice_monthly
                              : q.pricing_snapshot.monthly_total ?? 0,
                            q.currency
                          ) + '/mo'
                        : '\u2014'}
                    </TableCell>
                    <TableCell><StatusBadge status={q.status} /></TableCell>
                    <TableCell><ExpiryCellT expiryDate={q.expiry_date} /></TableCell>
                    <TableCell className="text-sm" style={{ color: 'hsl(var(--muted-fg))' }}>
                      {q.psm_name || q.psm_user_name || '\u2014'}
                    </TableCell>
                    <TableCell className="text-sm whitespace-nowrap" style={{ color: 'hsl(var(--muted-fg))' }}>
                      {fmtDate(q.created_at)}
                    </TableCell>
                    <TableCell onClick={e => e.stopPropagation()}>
                      {canDelete(q) && (
                        <Button variant="ghost" size="sm"
                          onClick={e => { e.stopPropagation(); setDeleteTarget({ id: q.id, quote_number: q.quote_number }); }}
                          style={{ color: 'hsl(var(--danger))' }}
                          title={q.status === 'ACCEPTED' ? 'Delete signed quote (Super Admin only)' : 'Delete quote'}
                          data-testid={`tenant-delete-quote-${q.id}`}>
                          <Trash2 size={13} />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      <Dialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Trash2 size={16} style={{ color: 'hsl(var(--danger))' }} />
              Delete Quote
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm" style={{ color: 'hsl(var(--muted-fg))' }}>
            Are you sure you want to delete <strong>{deleteTarget?.quote_number}</strong>?
            This action cannot be undone.
          </p>
          {deleteTarget && quotes.find(q => q.id === deleteTarget.id)?.status === 'ACCEPTED' && (
            <div className="rounded-lg px-3 py-2 text-xs flex items-start gap-2"
              style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#7f1d1d' }}>
              <AlertCircle size={13} className="mt-0.5 flex-shrink-0" />
              <span>This quote has been digitally signed. Deleting it will remove the audit record. Only proceed if you are certain this is required.</span>
            </div>
          )}
          <DialogFooter className="mt-2">
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button onClick={handleDelete} disabled={deleting}
              style={{ background: 'hsl(var(--danger))', color: 'white' }}
              data-testid="confirm-delete-tenant-quote">
              {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Delete Quote'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ContractsTenantTab({ tenantId, tenantName }) {
  const navigate = useNavigate();
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!tenantId) return;
    setLoading(true);
    contractsAPI.list({ tenant_id: tenantId, status: 'ALL', limit: 100 })
      .then(r => setContracts(r.data.contracts || []))
      .catch(() => setContracts([]))
      .finally(() => setLoading(false));
  }, [tenantId]);

  if (loading) return <div className="space-y-3 pt-2">{[1,2,3].map(i => <Skeleton key={i} className="h-12 w-full" />)}</div>;

  return (
    <div className="space-y-4 pt-2">
      <p className="text-sm" style={{ color: 'hsl(var(--muted-fg))' }}>
        {contracts.length === 0 ? `No contracts for ${tenantName} yet.` : `${contracts.length} contract(s) linked to ${tenantName}`}
      </p>
      {contracts.length === 0 ? (
        <div className="rounded-xl border bg-white p-10 text-center">
          <FileSignature size={32} className="mx-auto mb-3" style={{ color: 'hsl(var(--border))' }} />
          <p className="text-sm font-medium" style={{ color: NAVY }}>No contracts yet</p>
          <p className="text-xs mt-1" style={{ color: 'hsl(var(--muted-fg))' }}>Contracts are created automatically when a quote is signed.</p>
        </div>
      ) : (
        <div className="rounded-xl border bg-white overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow style={{ background: 'hsl(var(--muted))' }}>
                <TableHead>Contract #</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Partner</TableHead>
                <TableHead>Product</TableHead>
                <TableHead>Pricing</TableHead>
                <TableHead>Users</TableHead>
                <TableHead>Monthly</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Created</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {contracts.map(c => (
                <TableRow key={c.id} onClick={() => navigate(`/contracts/${c.id}`)}
                  className="data-table-row cursor-pointer transition-colors hover:bg-slate-50">
                  <TableCell>
                    <span className="font-mono text-sm font-semibold" style={{ color: NAVY }}>{c.contract_number}</span>
                  </TableCell>
                  <TableCell className="text-sm max-w-[160px] truncate">{c.contract_name || '\u2014'}</TableCell>
                  <TableCell className="text-sm">{c.customer_name || '\u2014'}</TableCell>
                  <TableCell>
                    {(() => {
                      const cat = c.quote_category;
                      const pc = c.product_code;
                      const isCustom = cat && cat !== 'PRODUCTION';
                      if (isCustom) {
                        return (
                          <span className="text-xs font-medium px-2 py-0.5 rounded-full"
                            style={{ background: '#f5f3ff', color: '#7c3aed', border: '1px solid #c4b5fd' }}>
                            {cat === 'TEST_SYSTEM' ? 'Custom Test' : 'Custom'}
                          </span>
                        );
                      }
                      return (
                        <span className="text-xs font-medium px-2 py-0.5 rounded-full"
                          style={pc !== 'STANDARD' ? { background: NAVY, color: 'white' } : { background: 'hsl(var(--muted))', color: 'hsl(var(--fg))' }}>
                          {pc}
                        </span>
                      );
                    })()}
                  </TableCell>
                  <TableCell>
                    {c.contract_source === 'manual' && (
                      <span className="text-xs font-medium px-1.5 py-0.5 rounded"
                        style={{ background: '#fff7ed', color: '#9a3412', border: '1px solid #fdba74' }}>
                        Manual Pricing
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-sm tabular-nums">{c.num_users ?? '\u2014'}</TableCell>
                  <TableCell className="text-sm font-semibold tabular-nums">
                    {c.monthly_total != null ? fmtMoney(c.monthly_total, c.currency) + '/mo' : '\u2014'}
                  </TableCell>
                  <TableCell>
                    <span className="text-xs font-medium px-2 py-0.5 rounded-full"
                      style={c.status === 'LIVE'
                        ? { background: '#f0fdf4', color: '#14532d', border: '1px solid #bbf7d0' }
                        : { background: '#f1f5f9', color: '#64748b', border: '1px solid #e2e8f0' }}>
                      {c.status}
                    </span>
                  </TableCell>
                  <TableCell className="text-sm" style={{ color: 'hsl(var(--muted-fg))' }}>{fmtDate(c.created_at)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

export default function TenantDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin, isManager, user: authUser } = useAuth();
  const [tenant, setTenant]   = useState(null);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [showDelete, setShowDelete] = useState(false);
  const [deleting, setDeleting]     = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'profile';
  const setActiveTab = (id) => setSearchParams(id === 'profile' ? {} : { tab: id }, { replace: true });

  const load = useCallback(async () => {
    try { const { data } = await tenantsAPI.get(id); setTenant(data); }
    catch { toast.error('Tenant not found'); navigate('/tenants'); }
    finally { setLoading(false); }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { customersAPI.list({ page_size: 200, status: 'active' }).then(r => setPartners(r.data.items || [])).catch(() => {}); }, []);

  const toggleStatus = async () => {
    try { await tenantsAPI.update(id, { status: tenant.status === 'active' ? 'inactive' : 'active' }); toast.success('Status updated'); load(); }
    catch { toast.error('Failed'); }
  };
  const handleDelete = async () => {
    setDeleting(true);
    try { await tenantsAPI.delete(id); toast.success('Tenant deleted'); navigate('/tenants'); }
    catch { toast.error('Failed'); } finally { setDeleting(false); }
  };

  if (loading) return <div className="space-y-4"><Skeleton className="h-16 w-full" /><Skeleton className="h-48 w-full" /></div>;
  if (!tenant) return null;

  const isActive  = tenant.status === 'active';
  const canManage = isAdmin || isManager;
  const isSuperAdmin = authUser?.role === 'SUPER_ADMIN';

  return (
    <div className="flex flex-col min-h-full" style={{ background: '#f8fafc' }} data-testid="tenant-detail-page">

      {/* ── Sticky Header ── */}
      <div className="bg-white flex-shrink-0" style={{ borderBottom: '1px solid #e2e8f0' }}>
        <div className="px-8 pt-6 pb-4">
          {/* Breadcrumb */}
          <Link to="/tenants">
            <button className="flex items-center gap-1.5 text-xs font-medium mb-4 hover:opacity-70 transition-opacity"
              style={{ color: 'hsl(var(--muted-fg))' }}>
              <ArrowLeft size={13} /> Tenants
            </button>
          </Link>
          {/* Identity row */}
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center text-lg font-bold flex-shrink-0"
                style={{ background: 'hsl(265 89% 68% / 0.12)', color: 'hsl(265 89% 45%)' }}>
                {tenant.company_name?.charAt(0).toUpperCase() || 'T'}
              </div>
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <h2 className="text-2xl font-bold" style={{ fontFamily: 'var(--font-display)', color: NAVY }}>{tenant.company_name}</h2>
                  <Badge variant="outline" className="text-xs font-medium"
                    style={isActive ? { background: '#f0fdf4', color: '#15803d', borderColor: '#bbf7d0' } : { background: '#f8fafc', color: '#475569', borderColor: '#e2e8f0' }}>
                    {isActive ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
                <div className="flex items-center gap-3 text-xs mt-1 flex-wrap" style={{ color: 'hsl(var(--muted-fg))' }}>
                  {tenant.country && <span>{tenant.country}</span>}
                  {tenant.partner_name && <span>Partner: {tenant.partner_name}</span>}
                  {tenant.email && <span>{tenant.email}</span>}
                </div>
              </div>
            </div>
            {canManage && (
              <div className="flex items-center gap-2 flex-shrink-0">
                {isSuperAdmin && (
                  <TenantGdprExportButton tenantId={tenant.id} tenantName={tenant.company_name} />
                )}
                <Button variant="outline" size="sm" onClick={toggleStatus} className="gap-1.5"
                  style={isActive ? { color: '#d97706', borderColor: '#fde68a' } : { color: '#15803d', borderColor: '#bbf7d0' }}>
                  {isActive ? <PowerOff size={13} /> : <Power size={13} />} {isActive ? 'Deactivate' : 'Activate'}
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setShowDelete(true)} style={{ color: 'hsl(var(--danger))' }}><Trash2 size={13} /></Button>
              </div>
            )}
          </div>
        </div>

        {/* Tab bar — bottom-border style */}
        <div className="px-8 flex gap-0.5" style={{ background: 'white' }}>
          {[
            { id: 'profile',   label: 'Profile' },
            { id: 'contacts',  label: `Contacts (${(tenant.contacts || []).length})` },
            { id: 'quotes',    label: 'Quotes' },
            { id: 'contracts', label: 'Contracts' },
          ].map(({ id, label }) => (
            <button key={id} onClick={() => setActiveTab(id)}
              data-testid={`tab-${id}`}
              className="px-4 py-3 text-sm font-medium transition-colors outline-none"
              style={{
                color: activeTab === id ? NAVY : '#64748b',
                fontWeight: activeTab === id ? 600 : 400,
                borderBottom: activeTab === id ? `2px solid ${NAVY}` : '2px solid transparent',
                background: 'transparent',
              }}>
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Tab content ── */}
      <div className="flex-1 px-8 py-8">
        {activeTab === 'profile'   && <ProfileTab tenant={tenant} partners={partners} onSaved={load} />}
        {activeTab === 'contacts'  && <TenantContactsTab tenant={tenant} onReload={load} />}
        {activeTab === 'quotes'    && <QuotesTab tenantId={tenant.id} tenantName={tenant.company_name} />}
        {activeTab === 'contracts' && <ContractsTenantTab tenantId={tenant.id} tenantName={tenant.company_name} />}
      </div>
      <Dialog open={showDelete} onOpenChange={setShowDelete}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><AlertCircle size={16} style={{ color: 'hsl(var(--danger))' }} /> Delete Tenant</DialogTitle></DialogHeader>
          <p className="text-sm" style={{ color: 'hsl(var(--muted-fg))' }}>Delete <strong>{tenant.company_name}</strong>? Existing quotes will not be affected.</p>
          <DialogFooter className="mt-2">
            <Button variant="outline" onClick={() => setShowDelete(false)}>Cancel</Button>
            <Button onClick={handleDelete} disabled={deleting} style={{ background: 'hsl(var(--danger))', color: 'white' }}>{deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Delete Tenant'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
